import {
  EClickMode,
  useHeatmapDataContext,
  useHeatmapSettingContext,
  useHeatmapVizContext,
} from '@gem-sdk/heatmap-react';
import { BlockStack, Box, Scrollable, Text } from '@shopify/polaris';
import { AreaEditModeToggle, ClickArea } from './ClickArea';
import { GBlockStack, GObserver } from '~/presentation/ui';
import { SelectClickRank, ClickRank } from './ClickRank';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useHeatmapLoadingContext } from '../../hooks';
import { HEATMAP_CLICK_CONFIG } from '../../configs';
import { EHeatmapLoadingKey } from '../../types';
import { useTranslation } from 'react-i18next';
import { GBox } from '~/presentation/common';
import { useEffect, useState } from 'react';
import { SPACING_INSET } from './Sidebar';

export const SidebarClickMode: React.FC = () => {
  const sortedElements = useHeatmapDataContext((s) => s.dataInfo?.sortedElements);
  const isLoading = useHeatmapLoadingContext((s) =>
    s.getLoadingByKey(EHeatmapLoadingKey.CLICK_ELEMENT),
  );
  const isDomLoaded = useHeatmapVizContext((s) => s.isDomLoaded);

  const [isIntersecting, setIsIntersecting] = useState(false);

  const { isEnabledHeatmapV2 } = useFeatureFlagChecker();

  if (!isLoading && isDomLoaded && (!sortedElements || sortedElements.length === 0)) {
    return <ClickModeEmpty />;
  }

  return (
    <>
      {isEnabledHeatmapV2 && <div className="gx-hm-divider" />}
      <Box overflowY="hidden">
        <GBlockStack height="100%">
          <GBox ariaShadow={isIntersecting ? undefined : 'shadow-bottom-100'} zIndex="100">
            <ClickModeHeader />
          </GBox>

          <div
            style={{
              position: 'relative',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Scrollable
              scrollbarGutter="stable"
              scrollbarWidth="thin"
              vertical
              className="max-h-full"
            >
              <GObserver setIsIntersecting={setIsIntersecting}></GObserver>
              <ClickModeContent isScrolled={!isIntersecting} />
            </Scrollable>
          </div>
        </GBlockStack>
      </Box>
    </>
  );
};

const ClickModeHeader = () => {
  const clickMode = useHeatmapSettingContext((state) => state.clickMode);

  switch (clickMode) {
    case EClickMode.Default:
      return (
        <Box padding={SPACING_INSET}>
          <SelectClickRank />
        </Box>
      );
    case EClickMode.Area:
      return <AreaEditModeToggle />;
  }
};

const ClickModeContent = ({ isScrolled }: { isScrolled: boolean }) => {
  const clickMode = useHeatmapSettingContext((state) => state.clickMode);
  const setClickMode = useHeatmapSettingContext((state) => state.setClickMode);

  useEffect(() => {
    if (clickMode) return;
    setClickMode(HEATMAP_CLICK_CONFIG.CLICK_MODE);
  }, [clickMode, setClickMode]);

  switch (clickMode) {
    case EClickMode.Default:
      return <ClickRank isScrolled={isScrolled} />;
    case EClickMode.Area:
      return <ClickArea isScrolled={isScrolled} />;
  }
};

const ClickModeEmpty = () => {
  const { t } = useTranslation();
  return (
    <GBlockStack height="100%">
      <div className="gx-hm-divider" />
      <GBlockStack height="100%" align="center">
        <Box padding={SPACING_INSET}>
          <BlockStack align="center" inlineAlign="center">
            <Text as="p" variant="bodyMd" fontWeight="medium">
              {t('Element data')}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t('No element data found')}
            </Text>
          </BlockStack>
        </Box>
      </GBlockStack>
    </GBlockStack>
  );
};
